import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'
import { ArrowUpRight } from 'lucide-react'
import { MaskLines } from '@/components/motion/reveal'
import { Magnetic } from '@/components/motion/magnetic'
import { ToneSection } from '@/components/generals/tone'
import { Logo } from '@/components/generals/logo'
import { NewsletterForm } from '@/components/generals/newsletter-form'
import { OfficeClocks } from '@/components/generals/office-clocks'

import {
    companyLinks,
    industryLinks,
    legalLinks,
    siteConfig
} from '@/constants/site'

const PreFooterCta: FC = (): ReactNode => {
    return (
        <ToneSection tone='violet' id='book-a-call' className='overflow-hidden px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
            <div className='grid gap-14 lg:grid-cols-12 lg:items-end'>
                <div className='lg:col-span-8'>
                    <p className='t-label mb-8 text-tone-muted'>
                        Zero risk. It’s that simple.
                    </p>
                    <MaskLines
                        as='h2'
                        className='font-heading t-display font-semibold uppercase wdth-75'
                        lines={['Turn payment', 'fraud into a', <span key='profit'>profit <span className='font-serif font-normal normal-case italic tracking-normal wdth-100'>center</span></span>]}
                    />
                </div>

                <div className='flex flex-col gap-10 lg:col-span-4 lg:items-end'>
                    <p className='t-lead max-w-sm text-tone-muted lg:text-right'>
                        Book a call with our team to find out how nSure.ai can deliver net incremental profit for your business with zero risk. It’s that simple.
                    </p>

                    <Magnetic strength={0.4}>
                        <Link
                            href='/contact'
                            data-cursor='Let’s talk'
                            className='group relative flex size-40 items-center justify-center rounded-full bg-void text-mist md:size-48'
                        >
                            <span className='absolute inset-0 scale-0 rounded-full bg-mist transition-transform duration-700 ease-out-expo group-hover:scale-100' aria-hidden />
                            <span className='relative flex flex-col items-center gap-2 text-center text-base font-semibold transition-colors duration-500 group-hover:text-void'>
                                <ArrowUpRight className='size-6 transition-transform duration-500 group-hover:rotate-45' aria-hidden />
                                Book a call
                            </span>
                        </Link>
                    </Magnetic>
                </div>
            </div>
        </ToneSection>
    )
}

interface FooterProps {
    cta?: boolean
}

export const Footer: FC<FooterProps> = ({ cta = true }): ReactNode => {
    const wordmark = useRef<HTMLDivElement>(null)
    const reduce = useReducedMotion()
    const { scrollYProgress } = useScroll({ target: wordmark, offset: ['start end', 'end end'] })
    const y = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['70%', '0%'])
    const scaleY = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.6, 1])

    return (
        <>
            {cta && <PreFooterCta />}

            <ToneSection tone='void' as='footer' className='overflow-hidden'>
                <div className='px-5 pt-24 md:px-8 lg:px-12 lg:pt-32'>
                    <div className='grid gap-16 lg:grid-cols-12'>
                        <div className='lg:col-span-5'>
                            <p className='t-label text-tone-muted'>
                                Stay connected
                            </p>
                            <p className='mt-6 max-w-md font-heading text-2xl font-medium leading-tight tracking-tight md:text-3xl'>
                                Subscribe to the latest updates and announcements from nSure.ai.
                            </p>
                            <NewsletterForm source='footer' className='mt-8' />
                        </div>

                        <nav aria-label='Footer' className='grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-4'>
                            {[
                                { title: 'Industries', links: industryLinks },
                                { title: 'Company', links: companyLinks },
                                { title: 'Help', links: legalLinks }
                            ].map(group => (
                                <div key={group.title}>
                                    <p className='t-label mb-5 text-tone-muted'>
                                        {group.title}
                                    </p>
                                    <ul className='flex flex-col gap-3'>
                                        {group.links.map(link => (
                                            <li key={link.href}>
                                                <Link href={link.href} className='link-line text-[0.9375rem]'>
                                                    {link.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </nav>

                        <div className='flex flex-col gap-8 lg:col-span-3'>
                            <div>
                                <p className='t-label mb-4 text-tone-muted'>
                                    Offices
                                </p>
                                <OfficeClocks layout='stack' />
                            </div>

                            <div className='flex flex-col gap-3'>
                                <a href={`mailto:${siteConfig.email}`} className='link-line self-start text-lg font-medium'>
                                    {siteConfig.email}
                                </a>
                                <div className='flex gap-5 t-label'>
                                    <a href={siteConfig.links.linkedin} target='_blank' rel='noopener noreferrer' className='link-line'>
                                        LinkedIn
                                    </a>
                                    <a href={siteConfig.links.x} target='_blank' rel='noopener noreferrer' className='link-line'>
                                        X / Twitter
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div ref={wordmark} className='mt-24 overflow-hidden px-4 md:px-6 lg:mt-32'>
                    <motion.div style={{ y, scaleY }} className='origin-bottom'>
                        <Logo className='h-auto w-full text-mist' title='nSure.ai' />
                    </motion.div>
                </div>

                <div className='mt-6 flex flex-col gap-3 border-t border-tone-line px-5 py-6 t-label text-tone-muted md:flex-row md:items-center md:justify-between md:px-8 lg:px-12'>
                    <span suppressHydrationWarning>
                        © {new Date().getFullYear()} nSure.ai. All rights reserved.
                    </span>
                    <span>
                        New York | London | Tel Aviv
                    </span>
                </div>
            </ToneSection>
        </>
    )
}
